import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getCatalog, getOwnership } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePets } from '../context/PetContext';
import { usePetPrices, useCosmeticCatalog, useFoodCatalog } from '../context/CatalogContext';
import { LeafIcon, ShieldIcon, CrownIcon, PetCountIcon, CosmeticsIcon, EmoteIcon, FoodCountIcon, AbilityIcon } from '../components/icons';
import { getPetComponent } from '../components/pets';
import { getCosmeticComponent, EMOTE_CATALOG, EMOTE_CATEGORIES } from '../components/cosmetics';
import PetRenderer from '../components/PetRenderer';
import { PetVisual, CosmeticVisual, useIn3dOnly, ViewModeToggle } from '../components/visuals';
import {
  RELEASE_TAG,
  RELEASE_PET_IDS,
  RELEASE_PETS,
  RELEASE_PETS_PNG_ONLY,
  RELEASE_COSMETIC_PREVIEWS,
  RELEASE_SHORT,
} from '../components/release/releaseCatalog';
import '../styles/release.css';
import './Catalog.css';

const CATALOG_SECTIONS = [
  { id: 'pets', label: 'Pets', Icon: PetCountIcon, color: '#AB47BC', count: 42 },
  { id: 'cosmetics', label: 'Cosmetics', Icon: CosmeticsIcon, color: 'var(--magenta-primary)', count: '150+' },
  { id: 'emotes', label: 'Emotes', Icon: EmoteIcon, color: '#42A5F5', count: 19 },
  { id: 'food', label: 'Food', Icon: FoodCountIcon, color: '#FF8A65', count: 12 },
  { id: 'abilities', label: 'Abilities', Icon: AbilityIcon, color: 'var(--orange-primary)', count: 6 },
];

// Map of release pet ID -> thumbnail image URL. Used by the catalog grid
// when a pet has no PetSvg component yet. 3D-only pets without a still
// PNG render fall through to a placeholder badge in the card.
const RELEASE_PET_THUMBS = (() => {
  const map = {};
  RELEASE_PETS.forEach((p) => { map[p.id] = p.gif || p.still; });
  RELEASE_PETS_PNG_ONLY.forEach((p) => { if (p.still) map[p.id] = p.still; });
  return map;
})();

function ReleasePetPlaceholder({ name, size = 48 }) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <div
      aria-label={name}
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        background: 'linear-gradient(135deg, rgba(224,64,251,0.18), rgba(224,64,251,0.04))',
        border: '1px solid rgba(224,64,251,0.40)',
        color: '#E040FB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Bitcount Grid Double', system-ui",
        fontWeight: 700,
        fontSize: Math.round(size * 0.5),
      }}
    >
      {initial}
    </div>
  );
}

// Build catalog rows for any release pets the backend has not yet exposed.
// Tier defaults to individual so they show the Earnable pill and price.
function buildLocalReleaseRows(existingIds) {
  const out = [];
  [...RELEASE_PETS, ...RELEASE_PETS_PNG_ONLY].forEach((p) => {
    if (existingIds.has(p.id)) return;
    out.push({
      id: p.id,
      name: p.name,
      tier: 'individual',
      variants: null,
      supports_baby: false,
      release_tag: RELEASE_TAG,
    });
  });
  return out;
}

// Presentation only. Tier, price and effects come from the backend
// catalog (/catalog/all) — this used to duplicate them, and the tiers
// drifted out of sync with the server for 11 of 12 items.
const FOOD_PRESENTATION = {
  pet_kibble: { icon: '\uD83E\uDDB4', favorites: 'All pets' },
  premium_chow: { icon: '\uD83C\uDF56', favorites: 'All pets' },
  fresh_fish: { icon: '\uD83D\uDC1F', favorites: 'Cat, Fox, Ocelot' },
  juicy_steak: { icon: '\uD83E\uDD69', favorites: 'Wolf, Polar Bear' },
  bamboo_treat: { icon: '\uD83C\uDF8D', favorites: 'Panda' },
  honey_drops: { icon: '\uD83C\uDF6F', favorites: 'Bee, Polar Bear' },
  sweet_berries: { icon: '\uD83C\uDF53', favorites: 'Fox, Rabbit, Parrot' },
  crystal_candy: { icon: '\uD83D\uDC8E', favorites: 'Elemental Slime' },
  dragon_fruit: { icon: '\uD83E\uDD6D', favorites: 'Baby Dragon, Wisp' },
  carrot_cake: { icon: '\uD83C\uDF82', favorites: 'Rabbit, Horse, Llama' },
  seaweed_wrap: { icon: '\uD83C\uDF3F', favorites: 'Axolotl, Turtle, Dolphin' },
  mushroom_stew: { icon: '\uD83C\uDF72', favorites: 'Cow, Sheep, Goat' },
};

const ABILITY_CATALOG = [
  { id: 'head_mount', name: 'Head Mount', price: 150, description: 'Pet rides on top of your head.', unlock: 'Level 3' },
  { id: 'night_vision', name: 'Night Vision', price: 200, description: 'Pet glows and gives you night vision.', unlock: 'Level 4' },
  { id: 'ore_radar', name: 'Ore Radar', price: 300, description: 'Pet detects nearby ores with particles.', unlock: 'Level 5' },
  { id: 'swift_companion', name: 'Swift Companion', price: 150, description: 'Speed I while pet follows you.', unlock: 'Level 6' },
  { id: 'pet_satchel', name: 'Pet Satchel', price: 250, description: '6-slot portable inventory on your pet.', unlock: 'Level 8' },
  { id: 'combat_buddy', name: 'Combat Buddy', price: 350, description: 'Pet attacks hostile mobs near you.', unlock: 'Level 10' },
];

const SLOT_LABELS = { head: 'Head', eyes: 'Eyes', neck: 'Neck', weapon: 'Weapon', back: 'Back', cape: 'Cape', feet: 'Feet', trail: 'Trail', none: 'Item' };

const TIER_LABELS = { free: 'Free', plus: 'Plus', premium: 'Premium', individual: 'Individual' };
const TIER_ICONS = {
  free: (props) => <LeafIcon size={12} color="#2E7D32" {...props} />,
  plus: (props) => <ShieldIcon size={12} color="#1565C0" {...props} />,
  premium: (props) => <CrownIcon size={12} color="#F57F17" {...props} />,
  individual: (props) => <CrownIcon size={12} color="#E040FB" {...props} />,
};

// Calculate how many columns fit in the grid
function useColumns() {
  const [cols, setCols] = useState(5);
  useEffect(() => {
    const update = () => {
      const w = Math.min(window.innerWidth, 1100) - 48; // container padding
      setCols(Math.max(2, Math.floor(w / 170)));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return cols;
}

function isReleaseItem(item) {
  if (!item) return false;
  if (item.release_tag === RELEASE_TAG) return true;
  // Frontend-only fallback while the backend release_tag column is rolling out.
  return item.id ? RELEASE_PET_IDS.has(item.id) : false;
}

export default function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = searchParams.get('type');
  const initialRelease = searchParams.get('release');
  const [pets, setPets] = useState([]);
  const [filter, setFilter] = useState('all');
  const [releaseFilter, setReleaseFilter] = useState(initialRelease === RELEASE_TAG);
  const [section, setSection] = useState(
    initialType === 'cosmetic' ? 'cosmetics' :
    initialType === 'emote' ? 'emotes' :
    initialType === 'food' ? 'food' :
    initialType === 'ability' ? 'abilities' :
    'pets'
  );
  const [selectedPet, setSelectedPet] = useState(null);
  const { isAuthenticated } = useAuth();
  const { adoptPet } = usePets();
  const navigate = useNavigate();
  const [adoptName, setAdoptName] = useState('');
  const [adoptError, setAdoptError] = useState('');
  const [adopting, setAdopting] = useState(false);
  const [ownership, setOwnership] = useState(null);
  const [emoteCategoryFilter, setEmoteCategoryFilter] = useState('All');
  const PET_PRICES = usePetPrices();
  const apiCosmetics = useCosmeticCatalog();
  const apiFoods = useFoodCatalog();
  const detailRef = useRef(null);
  const cols = useColumns();

  // Keep the URL in sync when the release chip toggles, so the link is
  // shareable and survives back-button navigation.
  const toggleReleaseFilter = () => {
    const next = !releaseFilter;
    setReleaseFilter(next);
    const params = new URLSearchParams(searchParams);
    if (next) params.set('release', RELEASE_TAG);
    else params.delete('release');
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    getCatalog()
      .then((res) => {
        const apiPets = res.data.pets || [];
        const existingIds = new Set(apiPets.map((p) => p.id));
        // Backend has not yet shipped rows for the Heart Tree release pets;
        // merge them in client-side so the catalog grid shows all 42.
        setPets([...apiPets, ...buildLocalReleaseRows(existingIds)]);
      })
      .catch(() => setPets(buildLocalReleaseRows(new Set())));
    if (isAuthenticated) {
      getOwnership()
        .then((res) => setOwnership(res.data))
        .catch(() => {});
    }
  }, [isAuthenticated]);

  // Use backend catalog as single source of truth
  const cosmeticCatalog = apiCosmetics;

  // In 3D, only what has a model to show.
  const in3d = useIn3dOnly();
  const filtered = pets
    .filter((p) => (filter === 'all' ? true : p.tier === filter))
    .filter((p) => (releaseFilter ? isReleaseItem(p) : true))
    .filter((p) => in3d.pet(p.id));

  const handleSelect = (pet) => {
    // Release pets (Heart Tree) have a richer detail page with GIF + 3D
    // preview. Route there directly instead of the inline SVG panel.
    if (RELEASE_PET_IDS.has(pet.id)) {
      navigate(`/catalog/pet/${pet.id}`);
      return;
    }
    if (selectedPet?.id === pet.id) {
      setSelectedPet(null); // toggle off
    } else {
      setSelectedPet(pet);
      setAdoptName('');
      setAdoptError('');
      // Scroll to detail after render
      setTimeout(() => {
        detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  };

  const isOwned = (petId) => {
    // Free pets are always available
    const pet = pets.find(p => p.id === petId);
    if (pet?.tier === 'free') return true;
    // Not logged in or still loading ownership
    if (!ownership) return false;
    return ownership.owned_pets?.[petId] ?? false;
  };

  const handleAdopt = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!adoptName.trim()) { setAdoptError('Give your pet a name.'); return; }
    if (!isOwned(selectedPet.id)) {
      setAdoptError(`You need to purchase ${selectedPet.name} first. Visit the Shop.`);
      return;
    }
    setAdoptError('');
    setAdopting(true);
    const result = await adoptPet(selectedPet.id, adoptName.trim(), ['curious', 'playful']);
    setAdopting(false);
    if (result.success) { navigate('/pets'); }
    else { setAdoptError(result.error); }
  };

  // Group filtered pets into rows, inserting the detail panel after the selected pet's row
  const selectedIndex = selectedPet ? filtered.findIndex(p => p.id === selectedPet.id) : -1;
  const selectedRow = selectedIndex >= 0 ? Math.floor(selectedIndex / cols) : -1;

  // Build rows with inline expansion
  const rows = [];
  for (let i = 0; i < filtered.length; i += cols) {
    const rowPets = filtered.slice(i, i + cols);
    const rowIndex = Math.floor(i / cols);
    rows.push(
      <div className="catalog-row" key={`row-${rowIndex}`}>
        {rowPets.map((pet) => {
          const PetSvg = getPetComponent(pet.id);
          const releaseThumb = RELEASE_PET_THUMBS[pet.id];
          const isSelected = selectedPet?.id === pet.id;
          return (
            <div
              key={pet.id}
              className={`catalog-card card ${isSelected ? 'selected' : ''}`}
              onClick={() => handleSelect(pet)}
            >
              <div className="catalog-card-avatar">
                {PetSvg
                  ? <PetVisual petId={pet.id} size={96} mood="happy" />
                  : releaseThumb
                    ? <img src={releaseThumb} alt={pet.name} style={{ width: 96, height: 96, objectFit: 'contain', imageRendering: 'pixelated' }} />
                    : <ReleasePetPlaceholder name={pet.name} size={96} />}
              </div>
              <h3 className="catalog-card-name">{pet.name}</h3>
              <span className={`tier-badge tier-${pet.tier}`}>
                {TIER_LABELS[pet.tier]}
              </span>
              {pet.tier === 'individual' && (
                <span
                  className="catalog-earnable-pill"
                  title="Available with treats. Earn treats by logging in and during events."
                >
                  Earnable
                </span>
              )}
              {pet.tier !== 'free' && (
                isOwned(pet.id)
                  ? <span className="catalog-owned-badge">Owned</span>
                  : <span className="catalog-price-badge">{PET_PRICES[pet.tier]} treats</span>
              )}
              {pet.variants && (
                <span className="variant-count">{pet.variants.length} variants</span>
              )}
              {pet.supports_baby && <span className="baby-badge">Baby mode</span>}
              {/* Arrow indicator for selected card */}
              {isSelected && <div className="selected-arrow" />}
            </div>
          );
        })}
      </div>
    );

    // Insert detail panel after the row containing the selected pet
    if (rowIndex === selectedRow && selectedPet) {
      rows.push(
        <div className="catalog-detail-inline" key="detail" ref={detailRef}>
          <div className="detail-inner card">
            <button className="detail-close" onClick={() => setSelectedPet(null)}>&times;</button>
            <div className="detail-header">
              <div className="detail-avatar-lg">
                {getPetComponent(selectedPet.id)
                  ? <PetRenderer petType={selectedPet.id} mood="neutral" size={180} />
                  : RELEASE_PET_THUMBS[selectedPet.id]
                    ? <img src={RELEASE_PET_THUMBS[selectedPet.id]} alt={selectedPet.name} style={{ width: 180, height: 180, objectFit: 'contain', imageRendering: 'pixelated' }} />
                    : <ReleasePetPlaceholder name={selectedPet.name} size={180} />}
              </div>
              <div>
                <h2>{selectedPet.name}</h2>
                <span className={`tier-badge tier-${selectedPet.tier}`}>
                  {TIER_LABELS[selectedPet.tier]}
                </span>
              </div>
            </div>

            {selectedPet.variants && (
              <div className="detail-variants">
                <h4>Variants</h4>
                <div className="variant-chips">
                  {selectedPet.variants.map((v) => (
                    <span key={v} className="variant-chip">
                      {v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="detail-features">
              {selectedPet.supports_baby && <span className="detail-feature">Supports baby mode</span>}
            </div>

            <div className="detail-adopt">
              {selectedPet.tier !== 'free' && !isOwned(selectedPet.id) ? (
                <div className="detail-locked">
                  <p className="detail-locked-msg">
                    This pet costs <strong>{PET_PRICES[selectedPet.tier]} treats</strong> to unlock.
                  </p>
                  <button className="btn btn-secondary" onClick={() => navigate('/shop')}>
                    Go to Shop
                  </button>
                </div>
              ) : (
              <div className="detail-adopt-row">
                <input
                  type="text"
                  className="detail-adopt-input"
                  placeholder="Name your pet..."
                  value={adoptName}
                  onChange={(e) => setAdoptName(e.target.value)}
                  maxLength={24}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAdopt(); }}
                />
                <button className="btn btn-primary" onClick={handleAdopt} disabled={adopting}>
                  {adopting ? 'Adopting...' : 'Adopt'}
                </button>
              </div>
              )}
              {adoptError && <div className="detail-adopt-error">{adoptError}</div>}
            </div>
          </div>
        </div>
      );
    }
  }

  // Cosmetics filtered by tier (uses API-merged tiers)
  const filteredCosmetics = cosmeticCatalog
    .filter((c) => c.slot !== 'none')
    .filter((c) => (filter === 'all' ? true : c.tier === filter))
    .filter((c) => (releaseFilter ? isReleaseItem(c) : true))
    .filter((c) => in3d.cosmetic(c.id));

  // Emotes filtered by category
  const filteredEmotes = emoteCategoryFilter === 'All'
    ? EMOTE_CATALOG
    : EMOTE_CATALOG.filter(e => e.category === emoteCategoryFilter);

  // Food comes from the backend catalog; merge in the local icon/favorites,
  // which the API does not serve. Falls back to presentation-only rows so the
  // grid still renders something if the catalog request failed.
  const foodItems = (apiFoods.length ? apiFoods : Object.keys(FOOD_PRESENTATION).map((id) => ({ id })))
    .map((f) => {
      const p = FOOD_PRESENTATION[f.id] || {};
      const effects = f.effects || {};
      const effect = [
        effects.hunger ? `+${effects.hunger} hunger` : null,
        effects.happiness ? `+${effects.happiness} happiness` : null,
      ].filter(Boolean).join(', ');
      return {
        ...f,
        tier: f.tier || 'free',
        price: f.price ?? 0,
        icon: p.icon,
        favorites: p.favorites,
        effect: effect || f.description,
      };
    });

  const filteredFood = filter === 'all'
    ? foodItems
    : foodItems.filter(f => f.tier === filter);

  const sectionInfo = CATALOG_SECTIONS.find(s => s.id === section);
  const subtitles = {
    pets: `${pets.length || 30} companions waiting for a home.`,
    cosmetics: 'Hats, glasses, capes, trails, and more.',
    emotes: 'Animations your pet can perform.',
    food: 'Feed your pet to keep them happy and healthy.',
    abilities: 'Special powers your pet can learn.',
  };

  return (
    <div className="catalog-page">
      <div className="container">
        <div className="catalog-header">
          <h1>Catalog</h1>
          <p className="catalog-subtitle">{subtitles[section]}</p>
          {['pets', 'cosmetics'].includes(section) && <ViewModeToggle className="catalog-view-toggle" />}
          {in3d.on && ['pets', 'cosmetics'].includes(section) && (
            <p className="catalog-3d-note">
              Showing the {section === 'pets' ? 'pets' : 'items'} with 3D models. Switch to 2D to see them all.
            </p>
          )}
        </div>

        {/* Section tabs */}
        <div className="catalog-section-tabs">
          {CATALOG_SECTIONS.map(({ id, label, Icon, color, count }) => (
            <button
              key={id}
              className={`section-tab ${section === id ? 'active' : ''}`}
              onClick={() => { setSection(id); setSelectedPet(null); setFilter('all'); setEmoteCategoryFilter('All'); }}
            >
              <Icon size={18} color={section === id ? 'white' : color} />
              <span className="section-tab-label">{label}</span>
              <span className="section-tab-count">{count}</span>
            </button>
          ))}
        </div>

        {/* Tier filters (shown for pets, cosmetics, food) */}
        {['pets', 'cosmetics', 'food'].includes(section) && (
          <div className="catalog-filters">
            {['all', 'free', 'plus', 'premium', 'individual'].map((f) => {
              const TierIcon = TIER_ICONS[f];
              return (
                <button
                  key={f}
                  className={`filter-btn ${filter === f ? 'active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? 'All' : (
                    <span className="filter-btn-content">
                      {TierIcon && <TierIcon />}
                      {TIER_LABELS[f]}
                    </span>
                  )}
                </button>
              );
            })}
            {section !== 'food' && (
              <button
                type="button"
                className={`catalog-release-chip ${releaseFilter ? 'active' : ''}`}
                onClick={toggleReleaseFilter}
                aria-pressed={releaseFilter}
              >
                <span className="dot" aria-hidden="true" />
                {RELEASE_SHORT}
              </button>
            )}
          </div>
        )}

        {/* Emote category filters */}
        {section === 'emotes' && (
          <div className="catalog-filters">
            {['All', ...EMOTE_CATEGORIES].map((cat) => (
              <button
                key={cat}
                className={`filter-btn ${emoteCategoryFilter === cat ? 'active' : ''}`}
                onClick={() => setEmoteCategoryFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Pet grid (original) */}
        {section === 'pets' && (
          <div className="catalog-grid">
            {rows}
          </div>
        )}

        {/* Cosmetics grid */}
        {section === 'cosmetics' && (
          <div className="catalog-item-grid">
            {filteredCosmetics.map((item) => {
              const previewUrl = RELEASE_COSMETIC_PREVIEWS[item.id];
              const CosmeticSvg = getCosmeticComponent(item.id);
              return (
                <Link
                  key={item.id}
                  to={`/catalog/cosmetic/${item.id}`}
                  className="catalog-item-card card"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="catalog-item-preview">
                    {CosmeticSvg
                      ? <CosmeticVisual cosmeticId={item.id} size={64} />
                      : previewUrl
                        ? <img src={previewUrl} alt={item.name} loading="lazy" style={{ maxWidth: 64, maxHeight: 64, objectFit: 'contain', imageRendering: 'pixelated' }} />
                        : <CosmeticsIcon size={32} color="var(--text-muted)" />}
                  </div>
                  <span className="catalog-item-name">{item.name}</span>
                  <div className="catalog-item-meta">
                    <span className={`tier-badge tier-${item.tier}`}>{TIER_LABELS[item.tier]}</span>
                    <span className="catalog-item-slot">{SLOT_LABELS[item.slot]}</span>
                  </div>
                  {item.tier === 'individual' && (
                    <span
                      className="catalog-earnable-pill"
                      title="Available with treats. Earn treats by logging in and during events."
                    >
                      Earnable
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        )}

        {/* Emotes grid */}
        {section === 'emotes' && (
          <div className="catalog-item-grid">
            {filteredEmotes.map((emote) => (
              <div key={emote.id} className="catalog-item-card card">
                <div className="catalog-item-preview emote-preview">
                  <EmoteIcon size={28} color="#42A5F5" />
                </div>
                <span className="catalog-item-name">{emote.name}</span>
                <div className="catalog-item-meta">
                  <span className="catalog-item-category">{emote.category}</span>
                  {emote.price > 0 ? (
                    <span className="catalog-item-price">{emote.price} treats</span>
                  ) : (
                    <span className="catalog-item-free">Free</span>
                  )}
                </div>
                <p className="catalog-item-desc">{emote.description}</p>
                {emote.loop && <span className="catalog-item-tag">Looping</span>}
                {emote.particles && <span className="catalog-item-tag sparkle">Particles</span>}
              </div>
            ))}
          </div>
        )}

        {/* Food grid */}
        {section === 'food' && (
          <div className="catalog-item-grid">
            {filteredFood.map((food) => (
              <div key={food.id} className="catalog-item-card card">
                <div className="catalog-item-preview food-preview">
                  <span style={{ fontSize: 28 }}>{food.icon}</span>
                </div>
                <span className="catalog-item-name">{food.name}</span>
                <div className="catalog-item-meta">
                  <span className={`tier-badge tier-${food.tier}`}>{TIER_LABELS[food.tier]}</span>
                  {food.price > 0 ? (
                    <span className="catalog-item-price">{food.price} treats</span>
                  ) : (
                    <span className="catalog-item-free">Free</span>
                  )}
                </div>
                <p className="catalog-item-desc">{food.effect}</p>
                <p className="catalog-item-favorites">Favorites: {food.favorites}</p>
              </div>
            ))}
          </div>
        )}

        {/* Abilities grid */}
        {section === 'abilities' && (
          <div className="catalog-item-grid abilities-grid">
            {ABILITY_CATALOG.map((ability) => (
              <div key={ability.id} className="catalog-item-card card ability-card">
                <div className="catalog-item-preview">
                  <AbilityIcon size={32} color="var(--orange-primary)" />
                </div>
                <span className="catalog-item-name">{ability.name}</span>
                <p className="catalog-item-desc">{ability.description}</p>
                <div className="catalog-item-meta">
                  <span className="catalog-item-price">{ability.price} treats</span>
                  <span className="catalog-item-tag">{ability.unlock}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
